import { AbstractControl, ValidationErrors } from '@angular/forms';

import { Observable, of } from 'rxjs';

import { catchError, delay, map, switchMap } from 'rxjs/operators';

import { DashboardPage } from 'app/providers/bridge-api/dashboard.service';
import { ConfigurationRoute } from 'app/providers/semantria-api/configuration-route.service';
import {
  ConfigurationService,
  SemantriaConfiguration,
} from 'app/providers/semantria-api/configuration.service';
import { Group } from 'app/providers/semantria-api/group.service';

export const configNameValidatorFactoryAsync = (
  configService: ConfigurationService,
  currentConfig?: SemantriaConfiguration,
  existingItems = []
) => {
  return nameValidatorFactoryWithAsync<SemantriaConfiguration>(
    existingItems,
    'name',
    1,
    64,
    currentConfig,
    configService
  );
};

export const configItemNameValidatorFactory = <T>(
  existingItems: T[],
  currentItem?: T,
  namePropNames = ['name']
) => {
  const nameProp = namePropNames.find((p) => currentItem?.[p]);
  const maxLength = Math.max(currentItem?.[nameProp]?.length ?? 64, 64);
  return nameValidatorFactory<T>(existingItems, namePropNames, 1, maxLength, currentItem);
};

export const configRouteNameValidatorFactory = (
  routes: ConfigurationRoute[],
  currentRoute?: ConfigurationRoute
) => {
  return nameValidatorFactory<ConfigurationRoute>(
    routes,
    ['name'],
    1,
    36,
    currentRoute,
    'route_id'
  );
};

export const groupNameValidatorFactory = (groups: Group[], currentGroup?: Group) => {
  return nameValidatorFactory<Group>(groups, ['group_name'], 1, 36, currentGroup, 'group_id');
};

export const dashboardPageNameValidatorFactory = (
  pages: DashboardPage[],
  currentPage: DashboardPage
) => {
  return nameValidatorFactory<DashboardPage>(pages, ['title'], 1, 50, currentPage);
};

function nameValidatorFactoryWithAsync<T extends { name?: string; language_id: string }>(
  existingItems: T[],
  nameProp: string,
  minLength: number,
  maxLength: number,
  currentItem?: T,
  configService?: ConfigurationService
) {
  return (control: AbstractControl): Observable<ValidationErrors> | null => {
    const controlValue = control.value !== undefined ? control.value.trim() : '';
    if (
      minLength !== undefined &&
      (control.value === undefined ||
        control.value === null ||
        control.value.trim().length < minLength)
    ) {
      return of({ minLength: { minLength } });
    }

    if (
      maxLength !== undefined &&
      control.value !== undefined &&
      control.value !== null &&
      control.value.length > maxLength
    ) {
      return of({ maxLength: { maxLength } });
    }

    if (
      existingItems.some(
        (item) =>
          !item['removed'] &&
          !item[nameProp].localeCompare(controlValue, undefined, { sensitivity: 'accent' }) &&
          item !== currentItem
      )
    ) {
      return of({ nameAlreadyExists: true });
    }

    if (
      configService &&
      currentItem &&
      !!controlValue.localeCompare(currentItem.name, undefined, { sensitivity: 'accent' })
    ) {
      return of(controlValue).pipe(
        delay(500),
        switchMap((_) => configService.configNameExists(controlValue, currentItem.language_id)),
        map((res: any) => (res ? { nameAlreadyExists: true } : null)),
        catchError(() => of(null))
      );
    }

    return of(null);
  };
}

function nameValidatorFactory<T>(
  existingItems: T[] = [],
  nameProps: string[],
  minLength: number,
  maxLength: number,
  currentItem?: T,
  idProp: string = 'id'
) {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const controlValue = control.value !== undefined ? control.value.trim() : '';
    if (
      minLength !== undefined &&
      (control.value == null || control.value.trim().length < minLength)
    ) {
      return { minLength: { minLength } };
    } else if (
      maxLength !== undefined &&
      control.value != null &&
      control.value.length > maxLength
    ) {
      return { maxLength: { maxLength } };
    } else if (
      existingItems.some(
        (item) =>
          !item['removed'] &&
          !item[nameProps.find((p) => item[p] != null)].localeCompare(controlValue, undefined, {
            sensitivity: 'accent',
          }) &&
          (!currentItem || item[idProp] !== currentItem[idProp])
      )
    ) {
      return { nameAlreadyExists: true };
    }

    return null;
  };
}

export const urlValidator = (control: AbstractControl): { [key: string]: any } | null => {
  if (control.value) {
    if (!control.value.endsWith('.json')) {
      return { urlNotCallback: true };
    }

    try {
      new URL(control.value);
    } catch {
      return { urlInvalid: true };
    }
  }

  return null;
};
